import { ScopeBag, ScopeHierarchy, ScopeItem } from "Shared/Components/Scope/Scopes.types";
import { getScopeTextValue } from "../ScopePivotValueDropdown";
import { getScopeDisplayName } from "Shared/Components/Scope/Hooks/getScopeTypeDisplayName";
import { Types } from "Shared/Utils";
import { extractSortedScopesFromBag } from "./extractSortedScopesFromBag";

const extraPropertiesOfInterest = new Set(["Alias"]);
const extraPropertiesDelimiter = "|";
const scopeTypeScopeValueDelimiter = ">";

export const convertScopeItemsToOptionItems = (scopeItems: ScopeItem[], scopeHierarchy?: ScopeHierarchy) =>
    scopeItems.map((scopeItem) => {
        const scopeItemHash: string[] = [`${scopeItem.ScopeType}${scopeTypeScopeValueDelimiter}${scopeItem.Value}`];
        const extraPropertiesHash = getExtraPropertiesHash(filterOutProperties(scopeItem.ExtraProperties || {}));
        if (extraPropertiesHash) {
            scopeItemHash.push(extraPropertiesHash);
        }
        const finalHash = scopeItemHash.join(extraPropertiesDelimiter);
        const scopeTypeDisplayName = scopeHierarchy && scopeItem.ScopeType ? getScopeDisplayName(scopeItem.ScopeType, scopeHierarchy) : scopeItem.ScopeType;
        return {
            key: finalHash,
            label: `${scopeTypeDisplayName} ${scopeTypeScopeValueDelimiter} ${getScopeTextValue(scopeItem).toUpperCase()}`,
            value: finalHash
        };
    });

export const convertScopeItemsToString = (scopeItems: ScopeItem[], scopeHierarchy?: string) => {
    const scopes = scopeItems.map((scopeItem) => {
        const scopeItemHash: string[] = [`${scopeItem.ScopeType}${scopeTypeScopeValueDelimiter}${scopeItem.Value}`];
        const extraPropertiesHash = getExtraPropertiesHash(filterOutProperties(scopeItem.ExtraProperties || {}));
        if (extraPropertiesHash) {
            scopeItemHash.push(extraPropertiesHash);
        }
        const finalHash = scopeItemHash.join(extraPropertiesDelimiter);
        return finalHash;
    });

    if (scopeHierarchy) {
        scopes.push(`Hierarchy${scopeTypeScopeValueDelimiter}${scopeHierarchy}`);
    }
    const scopesString = scopes.join(";");
    return scopesString;
};

const convertStringKeyToScopeItem = (key: string): ScopeItem | undefined => {
    const split = key.split(scopeTypeScopeValueDelimiter);
    if (split[1] !== undefined) {
        const valAndExtraPropertiesSplit = split[1].split(extraPropertiesDelimiter);
        return {
            ScopeType: split[0],
            Value: valAndExtraPropertiesSplit[0],
            ExtraProperties: valAndExtraPropertiesSplit[1] !== undefined ? getExtraPropertiesFromHash(valAndExtraPropertiesSplit[1]) : undefined
        };
    }
    return undefined;
};

export const convertScopeKeysToScopeBag = (scopeKeys: string[], cloudServiceEnvironment: string): ScopeBag | undefined => {
    const bag: ScopeBag = { ScopeHierarchyId: "", Scopes: [] };
    scopeKeys.forEach((scopeKey) => {
        const scope = convertStringKeyToScopeItem(scopeKey);
        if (scope) {
            if (scope.ScopeType === "Hierarchy") {
                bag.ScopeHierarchyId = scope.Value ?? "";
            } else {
                bag.Scopes?.push(scope);
            }
        }
    });
    const sorted = extractSortedScopesFromBag(cloudServiceEnvironment, bag);
    return sorted;
};

export const convertScopeStringToScopeBag = (scopeString: string, cloudServiceEnvironment: string): ScopeBag | undefined => {
    const bag = convertScopeKeysToScopeBag(scopeString.split(";"), cloudServiceEnvironment);
    return bag;
};

export const convertStringKeyToOptionItem = (val: string): OptionItem | undefined => {
    const converted = convertStringKeyToScopeItem(val);
    if (converted) {
        return convertScopeItemsToOptionItems([converted])[0];
    }
    return undefined;
};

export const convertOptionItemsToScopeItems = (optionItems: OptionItem[]): ScopeItem[] =>
    optionItems
        .map((optionItem) => (typeof optionItem.value === "string" ? convertStringKeyToScopeItem(optionItem.value) : undefined))
        .filter(Types.nonNullable);

const filterOutProperties = (properties: Record<string, string>) => {
    const initialObj: Record<string, string> = {};
    return Object.keys(properties)
        .filter((key) => extraPropertiesOfInterest.has(key))
        .reduce((obj, key) => {
            obj[key] = properties[key] ?? "";
            return obj;
        }, initialObj);
};

const getExtraPropertiesHash = (properties: Record<string, string>) => (Object.keys(properties).length ? JSON.stringify(properties) : "");

const getExtraPropertiesFromHash = (hash: string) => {
    const prop = JSON.parse(hash) as Record<string, string>;
    return prop;
};
